import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { useAuth } from '../context/AuthContext';
import useApi from '../hooks/useApi';

const EditStudentModal = ({
  show,
  onClose,
  onEditSuccess,
  enrollmentData,
  isSchoolAdmin = true
}) => {
  const { user } = useAuth();
  const { bearerAuth, publicAuth } = useApi();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    zipcode: "",
    dob: "",
    gender: "",
    adminNote: "",
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedCourseDuration, setSelectedCourseDuration] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [enrollmentFor, setEnrollmentFor] = useState("self");
  const [isEditable, setIsEditable] = useState(false);

  const enrollmentOptions = [
    { value: "self", label: "Self" },
    { value: "parent", label: "Parent" },
    { value: "other", label: "Other" }
  ];

  useEffect(() => {
    if (show && enrollmentData) {
      checkEditPermission();
      fetchCourses();
      loadEnrollmentData();
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = "15px";
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [show, enrollmentData]);

  useEffect(() => {
    if (selectedCourseId && selectedCourseDuration > 0) {
      calculateDateRange();
    }
  }, [selectedCourseId, selectedCourseDuration, includeWeekends, selectedDate]);

  const checkEditPermission = () => {
    if (!enrollmentData || !user) {
      setIsEditable(false);
      return;
    }
    
    // Check if current user is the same school that registered this student
    const isCreator = enrollmentData.user?.register_by === user.id;
    setIsEditable(isCreator);
    
    if (!isCreator) {
      Swal.fire({
        icon: 'warning',
        title: 'Access Denied',
        text: 'Only the school that registered this student can edit the enrollment.',
        timer: 3000
      });
    }
  };

 const loadEnrollmentData = async () => {
  if (!enrollmentData) return;
  
  setLoadingData(true);
  try {
    // Safely access nested properties
    const userData = enrollmentData.user || {};
    const courseData = enrollmentData.course || {};
    
    // Set form data from enrollment with safe defaults
    setFormData({
      name: userData.full_name || userData.username || "",
      email: userData.email || "",
      phone: userData.contact || "",
      zipcode: userData.zipcode || "",
      dob: enrollmentData.dob || "",
      gender: enrollmentData.gender || "",
      adminNote: enrollmentData.admin_note || "",
    });

    setEnrollmentFor(enrollmentData.enrollment_for || "self");
    setSelectedCourseId(enrollmentData.course_id || "");
    
    // Set include weekends
    setIncludeWeekends(enrollmentData.inc_sat_sun === 1);

    // Parse admin_exact_start_at
    if (enrollmentData.admin_exact_start_at) {
      const dateStr = enrollmentData.admin_exact_start_at;
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
        await fetchTimeSlots(date);
      }
    }

    // Parse admin_time_slot to find selected slot
    if (enrollmentData.admin_time_slot) {
      const [startTime, endTime] = enrollmentData.admin_time_slot.split(' - ');
      if (startTime && endTime) {
        // We'll match this with available slots after fetch
        setTimeout(() => {
          const matchingSlot = availableSlots.find(slot => 
            slot.start_time === startTime.trim() && 
            slot.end_time === endTime.trim()
          );
          if (matchingSlot) {
            setSelectedTimeSlot(matchingSlot);
          }
        }, 500);
      }
    }

  } catch (error) {
    console.error("Error loading enrollment data:", error);
  } finally {
    setLoadingData(false);
  }
};

 const fetchCourses = async () => {
  try {
    const res = await publicAuth(`courses`);
    // Handle different response structures
    const list = res.data?.courses || res.data?.data || [];
    
    // Ensure each course has the expected properties
    const processedList = list.map(course => ({
      ...course,
      // Ensure price is a string/number, not an object
      displayPrice: course.total_price || course.base_price || 'N/A'
    }));
    
    setCourses(processedList);
    
    // Set course duration if course is already selected
    if (enrollmentData?.course_id) {
      const course = processedList.find(c => c.id === enrollmentData.course_id);
      if (course) {
        setSelectedCourseDuration(parseInt(course.duration) || 0);
      }
    }
  } catch (err) {
    console.error("Failed to fetch courses:", err);
  }
};

 const getCourseDisplayPrice = (course) => {
    const value = course?.price?.total_price ?? course?.total_price ?? course?.price;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return "Price N/A";
    }
    return `₹${Math.round(numeric)}`;
  };
  const calculateDateRange = () => {
    if (!selectedCourseDuration) {
      setDateRange([]);
      return;
    }
    let daysToAdd = selectedCourseDuration;
    let date = new Date(selectedDate);
    let daysCounted = 0;
    let range = [new Date(date)];
    while (daysCounted < daysToAdd - 1) {
      date.setDate(date.getDate() + 1);
      if (includeWeekends || (date.getDay() !== 0 && date.getDay() !== 6)) {
        daysCounted++;
        range.push(new Date(date));
      }
    }
    setDateRange(range);
  };

  const fetchTimeSlots = async (date) => {
    try {
      const formattedDate = date.toISOString().split("T")[0];
      const res = await bearerAuth(`check-timeslots`, "POST", {
        date: formattedDate,
        assignSchool: user?.id,
      });
      const slots = res.data?.slots || res.data?.data || [];
      setAvailableSlots(slots);
      
      // Try to select the existing time slot
      if (enrollmentData?.admin_time_slot) {
        const [existingStart, existingEnd] = enrollmentData.admin_time_slot.split(' - ');
        const existingSlot = slots.find(slot => 
          slot.start_time === existingStart.trim() && 
          slot.end_time === existingEnd.trim()
        );
        if (existingSlot) {
          setSelectedTimeSlot(existingSlot);
        }
      } else {
        setSelectedTimeSlot(null);
      }
    } catch (err) {
      console.error("Slot fetch error:", err);
      setAvailableSlots([]);
    }
  };

  const formatSlotLabel = (slot) => {
    const base = `${slot.start_time} - ${slot.end_time}`;
    const capacity = Number(slot.capacity);
    const bookedCount = Number(slot.booked_count || 0);
    if (capacity > 0) {
      return `${base} (${bookedCount}/${capacity})${slot.booked ? " - Full" : ""}`;
    }
    return base;
  };

  const handleEnrollmentForChange = (e) => {
    if (!isEditable) return;
    setEnrollmentFor(e.target.value);
    setErrors((prev) => ({ ...prev, enrollmentFor: "" }));
  };

  const handleChange = (e) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    if (!isEditable) return false;
    
    const { name, email, phone, zipcode, dob, gender, adminNote } = formData;
    const newErrors = {};
    
    if (!name.trim()) newErrors.name = "Name is required";
    // else if (!/^[a-zA-Z\s]+$/.test(name))
    //   newErrors.name = "Only letters and spaces allowed";
    
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email";
    
    if (!phone.trim()) newErrors.phone = "Phone is required";
    else if (!/^[6-9]\d{9}$/.test(phone))
      newErrors.phone = "Enter valid 10-digit Indian number";
    
    if (!zipcode) newErrors.zipcode = "Enter a 6-digit ZIP code";
    else if (!/^[1-9][0-9]{5}$/.test(zipcode))
      newErrors.zipcode = "Invalid Indian ZIP code";

    if (!dob) newErrors.dob = "Date of birth is required";
    if (!gender) newErrors.gender = "Gender is required";
    
    if (!selectedCourseId) newErrors.course = "Please select a course";
    if (!enrollmentFor) newErrors.enrollmentFor = "Please select enrollment for";
    if (!selectedTimeSlot) newErrors.timeSlot = "Please select a time slot";
    if (!adminNote.trim()) newErrors.adminNote = "Admin note is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditable) return;
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      const payload = {
        
        full_name: formData.name,
        email: formData.email,
        contact: formData.phone,
        zipcode: formData.zipcode,
        dob: formData.dob,
        gender: formData.gender,
        course_id: selectedCourseId,
        enrollment_for: enrollmentFor,
        inc_sat_sun: includeWeekends,
        admin_exact_start_at: `${selectedDate.toISOString().split("T")[0]} ${selectedTimeSlot.start_time}`,
        admin_time_slot: `${selectedTimeSlot.start_time} - ${selectedTimeSlot.end_time}`,
        admin_note: formData.adminNote,
      };
      
      const response = await bearerAuth(`driving-school/edit-student/${enrollmentData.user_id}`, "POST", payload);
      
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "Student Updated Successfully!",
          text: "Student enrollment has been updated successfully.",
          showConfirmButton: false,
          timer: 2000,
        });
        
        if (onEditSuccess) {
          onEditSuccess();
        }
        onClose();
      } else {
        Swal.fire("Error", response.data.message || "Student update failed", "error");
      }
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Something went wrong",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (date) => {
    if (!isEditable) return;
    setSelectedDate(date);
    fetchTimeSlots(date);
    calculateDateRange();
  };

  const handleCourseSelect = (e) => {
    if (!isEditable) return;
    const courseId = e.target.value;
    setSelectedCourseId(courseId);
    setErrors((prev) => ({ ...prev, course: "" }));
    if (courseId) {
      const selectedCourse = courses.find((c) => c.id === courseId);
      if (selectedCourse) {
        setSelectedCourseDuration(parseInt(selectedCourse.duration) || 0);
      }
    } else {
      setSelectedCourseDuration(0);
    }
  };

  const toggleIncludeWeekends = () => {
    if (!isEditable) return;
    setIncludeWeekends(!includeWeekends);
  };

  const renderDayContents = (day, date) => {
    const isInRange = dateRange.some((d) => d.toDateString() === date.toDateString());
    const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
    return (
      <div
        style={{
          backgroundColor: isSelected ? "#0d6efd" : isInRange ? "#e6f2ff" : "transparent",
          fontWeight: isSelected ? "bold" : "normal",
          color: isSelected ? "#fff" : isInRange ? "#0d6efd" : "inherit",
          borderRadius: "50%",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {day}
      </div>
    );
  };

  if (!show) return null;

  return (
    <>  
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
          overflow-y: auto;
          padding: 20px;
        }
        .modal-box {
          background: white;
          border-radius: 8px;
          padding: 25px;
          width: 100%;
          max-width: 800px;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          position: relative;
          overflow: hidden;
          margin: auto;
        }
        .registerclose {
          position: absolute;
          top: 1px;
          right: 20px;
          font-size: 1.5rem;
          background: none;
          border: none;
          cursor: pointer;
          color: #6c757d;
          transition: color 0.2s;
          z-index: 1;
        }
        .registerclose:hover {
          color: #495057;
        }
        .modal-content {
          position: relative;
          z-index: 1;
        }
        .form-control:disabled,
        .form-select:disabled {
          background-color: #f8f9fa;
          cursor: not-allowed;
        }
        .time-slot-btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        .form-section {
          margin-bottom: 20px;
          padding: 15px;
          border-radius: 8px;
          background-color: #f8f9fa;
        }
        .form-section-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 15px;
          color: #495057;
          border-bottom: 1px solid #dee2e6;
          padding-bottom: 8px;
        }
        .edit-warning {
          background-color: #fff3cd;
          border: 1px solid #ffc107;
          border-radius: 6px;
          padding: 10px;
          margin-bottom: 15px;
          font-size: 0.9rem;
        }
      `}</style>   
      
      <div className="modal-overlay">
        <div className="modal-box">
          <button
            onClick={onClose}
            className="registerclose"
            aria-label="Close modal"
          >
            ×
          </button>
          
          <div className="modal-content">
            <h2 className="text-center mb-4" style={{ color: "#343a40" }}>
              Edit Student Enrollment
            </h2>
            
            {!isEditable && (
              <div className="edit-warning">
                <strong>⚠️ Edit Access Restricted:</strong> Only the driving school that registered this student can make edits.
              </div>
            )}
            
            {loadingData ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading enrollment data...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Personal Information Section */}
                <div className="form-section">
                  <h5 className="form-section-title">Personal Information</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Full Name*</label>
                      <input
                        type="text"
                        name="name"
                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                        placeholder="Full Name"
                        value={formData.name}
                        onChange={handleChange}
                        disabled={isLoading || !isEditable}
                      />
                      {errors.name && <div className="text-danger">{errors.name}</div>}
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Email ID*</label>
                      <input
                        type="email"
                        name="email"
                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isLoading || !isEditable}
                      />
                      {errors.email && <div className="text-danger">{errors.email}</div>}
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Mobile Number*</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text">+91</span>
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                          placeholder="Mobile Number"
                          value={formData.phone}
                          onChange={handleChange}
                          maxLength={10}
                          disabled={isLoading || !isEditable}
                        />
                      </div>
                      {errors.phone && <div className="text-danger">{errors.phone}</div>}
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Zipcode*</label>
                      <input
                        type="text"
                        name="zipcode"
                        className={`form-control ${errors.zipcode ? "is-invalid" : ""}`}
                        placeholder="Zipcode"
                        value={formData.zipcode}
                        onChange={handleChange}
                        maxLength={6}
                        disabled={isLoading || !isEditable}
                      />
                      {errors.zipcode && <div className="text-danger">{errors.zipcode}</div>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Date of Birth*</label>
                      <input
                        type="date"
                        name="dob"
                        className={`form-control ${errors.dob ? "is-invalid" : ""}`}
                        value={formData.dob}
                        onChange={handleChange}
                        disabled={isLoading || !isEditable}
                      />
                      {errors.dob && <div className="text-danger">{errors.dob}</div>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Gender*</label>
                      <select
                        name="gender"
                        className={`form-control ${errors.gender ? "is-invalid" : ""}`}
                        value={formData.gender}
                        onChange={handleChange}
                        disabled={isLoading || !isEditable}
                      >
                        <option value="">-- Select Gender --</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.gender && <div className="text-danger">{errors.gender}</div>}
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Enrollment For*</label>
                      <select
                        className={`form-control ${errors.enrollmentFor ? "is-invalid" : ""}`}
                        value={enrollmentFor}
                        onChange={handleEnrollmentForChange}
                        disabled={isLoading || !isEditable}
                      >
                        {enrollmentOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {errors.enrollmentFor && <div className="text-danger">{errors.enrollmentFor}</div>}
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">Admin Note*</label>
                      <textarea
                        name="adminNote"
                        className={`form-control ${errors.adminNote ? "is-invalid" : ""}`}
                        placeholder="Enter admin notes..."
                        value={formData.adminNote}
                        onChange={handleChange}
                        disabled={isLoading || !isEditable}
                        rows="3"
                      />
                      {errors.adminNote && <div className="text-danger">{errors.adminNote}</div>}
                    </div>
                  </div>
                </div>
                
                {/* Course Selection Section */}
                <div className="form-section">
                  <h5 className="form-section-title">Course Selection</h5>
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Choose a Course*</label>
                      <select
                        className={`form-control ${errors.course ? "is-invalid" : ""}`}
                        value={selectedCourseId}
                        onChange={handleCourseSelect}
                        disabled={isLoading || !isEditable}
                      >
                        <option value="">-- Select a Course --</option>
                        {courses.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.title} ({course.duration} days) - {getCourseDisplayPrice(course)}
                          </option>
                        ))}
                      </select>
                      {errors.course && <div className="text-danger">{errors.course}</div>}
                    </div>
                  </div>
                </div>
                
                {/* Date & Time Selection Section */}
                <div className="form-section date-time-section">
                  <h5 className="form-section-title">Schedule Selection</h5>
                  
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="includeWeekends"
                      checked={includeWeekends}
                      onChange={toggleIncludeWeekends}
                      disabled={!selectedCourseId || !isEditable}
                    />
                    <label className="form-check-label" htmlFor="includeWeekends">
                      Include Saturdays & Sundays
                    </label>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-5 mb-3 mb-md-0">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="card-title">Select Date*</h6>
                          <DatePicker
                            selected={selectedDate}
                            onChange={handleDateChange}
                            inline
                            className="w-100 custom-calendar"
                            renderDayContents={renderDayContents}
                            dayClassName={(date) => {
                              const isInRange = dateRange.some(
                                (d) => d.toDateString() === date.toDateString()
                              );
                              return isInRange ? "react-datepicker__day--highlight-range" : "";
                            }}
                            disabled={!isEditable}
                          />
                          {selectedCourseDuration > 0 && dateRange.length > 0 && (
                            <div className="mt-3 small text-muted">
                              <p className="mb-1">
                                <strong>Course Duration:</strong> {selectedCourseDuration} days
                              </p>
                              <p className="mb-0">
                                <strong>Period:</strong> {dateRange[0].toDateString()} to{" "}
                                {dateRange[dateRange.length - 1].toDateString()}
                                {!includeWeekends && " (excluding weekends)"}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-7">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="card-title">Available Time Slots*</h6>
                          {availableSlots.length === 0 ? (
                            <div className="alert alert-warning">
                              No slots available for selected date
                            </div>
                          ) : (
                            <div className="time-slots-container">
                              <div className="row">
                                {availableSlots.map((slot) => (
                                  <div key={slot.id} className="col-6 mb-2">
                                    <button
                                      type="button"
                                      className={`btn w-100 time-slot-btn ${
                                        selectedTimeSlot?.id === slot.id
                                          ? "btn-primary"
                                          : slot.booked
                                          ? "btn-outline-secondary disabled"
                                          : "btn-outline-primary"
                                      }`}
                                      onClick={() => !slot.booked && isEditable && setSelectedTimeSlot(slot)}
                                      disabled={slot.booked || !isEditable}
                                    >
                                      {formatSlotLabel(slot)}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {errors.timeSlot && <div className="text-danger mt-2">{errors.timeSlot}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  {isEditable && (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Updating...
                        </>
                      ) : (
                        "UPDATE STUDENT"
                      )}
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EditStudentModal;
